/**
 * Mobile-first SVG tornado chart for the sensitivity view (REN-15).
 *
 * Four horizontal bars, one per perturbation axis defined in
 * `math/sensitivity.ts`. Each bar spans from the break-even year at
 * `lever − delta` to the break-even year at `lever + delta`, with the
 * unperturbed baseline drawn as a vertical marker across every row. Bars are
 * sorted top-to-bottom by absolute range so the widest — the most sensitive
 * lever — sits at the top; that's the "tornado" reading a reader expects.
 *
 * "Never within horizon" is rendered as a dedicated slot one year past the
 * horizon end, so a `null` on either bound is visible rather than silently
 * clamped to the horizon boundary. The axis strip below the plot marks the
 * "never" tick explicitly.
 *
 * Layout notes:
 *  • Row labels + delta live on a single line ABOVE the bar so the bar can
 *    span nearly the full plot width. This is the smallest layout that
 *    keeps the tornado readable at a 375px viewport without truncating
 *    "Home appreciation ±1%/yr".
 *  • Bar-end value annotations sit inside the bar when there's room; the
 *    fallback is just outside the bar edge on the opposite side. This
 *    behavior lives in `drawRow`.
 *  • A "no crossover in horizon" caption fires when every row collapses to
 *    a point at the "never" slot — a legitimately-common outcome for the
 *    default scenario (~7yr horizon in a deep rent-favored market).
 *
 * Zero-dependency by design — same reasoning as `ui/chart.ts`. This is a
 * few SVG paths and text nodes on a fixed viewBox; the browser handles
 * scaling and crispness.
 */

import type { SensitivityRow } from "../math/sensitivity";

const SVG_NS = "http://www.w3.org/2000/svg";

export interface TornadoSpec {
  readonly rows: readonly SensitivityRow[];
  readonly horizonYears: number;
}

const LAYOUT = {
  width: 360,
  padTop: 12,
  padBottom: 28,
  plotLeft: 32,
  plotRightMargin: 44,
  rowLabelHeight: 14,
  rowBarHeight: 14,
  rowGap: 10,
} as const;

const AXIS_TICK_COUNT = 4;

const ROW_HEIGHT =
  LAYOUT.rowLabelHeight + LAYOUT.rowBarHeight + LAYOUT.rowGap;

/**
 * Build the tornado chart. Returns a wrapper element the results view can
 * append directly.
 */
export function renderTornadoChart(spec: TornadoSpec): HTMLElement {
  const wrapper = document.createElement("div");
  wrapper.className = "tornado";

  if (spec.rows.length === 0) {
    return wrapper;
  }

  const sorted = sortBySensitivity(spec.rows, spec.horizonYears);
  const height =
    LAYOUT.padTop + sorted.length * ROW_HEIGHT + LAYOUT.padBottom;
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", `0 0 ${LAYOUT.width} ${height}`);
  svg.setAttribute("role", "img");
  const description = describeChart(spec, sorted);
  svg.setAttribute("aria-label", description);
  const title = document.createElementNS(SVG_NS, "title");
  title.textContent = description;
  svg.append(title);
  svg.classList.add("tornado-svg");

  const xScale = makeXScale(spec.horizonYears);
  const baseline = spec.rows[0]!.baseline;
  const baselineX = xScale(yearPos(baseline, spec.horizonYears));

  drawAxis(svg, spec.horizonYears, xScale, height);
  drawBaselineMarker(svg, baselineX, sorted.length, baseline);

  sorted.forEach((row, index) => drawRow(svg, row, index, xScale, spec.horizonYears));

  wrapper.append(svg);

  if (allDegenerate(sorted)) {
    const note = document.createElement("p");
    note.className = "tornado-degenerate-note";
    note.textContent =
      "None of these adjustments produce a break-even year inside your horizon — rent still wins across the whole ± range.";
    wrapper.append(note);
  }

  return wrapper;
}

/**
 * Map a break-even year to its year-axis position. `null` (never within
 * horizon) is placed one year past the horizon end so it renders in a
 * dedicated slot rather than clamping to the horizon boundary.
 */
export function yearPos(year: number | null, horizonYears: number): number {
  return year === null ? horizonYears + 1 : year;
}

function neverPos(horizonYears: number): number {
  return horizonYears + 1;
}

function makeXScale(horizonYears: number): (year: number) => number {
  const maxYear = neverPos(horizonYears);
  const plotRight = LAYOUT.width - LAYOUT.plotRightMargin;
  const plotWidth = plotRight - LAYOUT.plotLeft;
  return (year) => LAYOUT.plotLeft + (year / maxYear) * plotWidth;
}

/**
 * Sort rows by absolute range on the year axis (widest first). Ties break
 * by the incoming order so the tornado is stable across recomputes when
 * two levers happen to have the same magnitude.
 */
export function sortBySensitivity(
  rows: readonly SensitivityRow[],
  horizonYears: number,
): SensitivityRow[] {
  return [...rows].sort((a, b) => magnitude(b, horizonYears) - magnitude(a, horizonYears));
}

function magnitude(row: SensitivityRow, horizonYears: number): number {
  const lowY = yearPos(row.low, horizonYears);
  const highY = yearPos(row.high, horizonYears);
  return Math.abs(highY - lowY);
}

function allDegenerate(rows: readonly SensitivityRow[]): boolean {
  return rows.every((r) => r.low === null && r.high === null);
}

function drawAxis(
  svg: SVGSVGElement,
  horizonYears: number,
  xScale: (year: number) => number,
  height: number,
): void {
  const group = document.createElementNS(SVG_NS, "g");
  group.setAttribute("class", "tornado-axis");
  const y = height - LAYOUT.padBottom + 18;

  const step = Math.max(1, Math.round(horizonYears / AXIS_TICK_COUNT));
  const years = new Set<number>();
  for (let year = 0; year <= horizonYears; year += step) years.add(year);
  years.add(horizonYears);
  const sortedYears = Array.from(years).sort((a, b) => a - b);

  for (const year of sortedYears) {
    const x = xScale(year);
    const label = document.createElementNS(SVG_NS, "text");
    label.setAttribute("x", x.toFixed(2));
    label.setAttribute("y", String(y));
    label.setAttribute("text-anchor", "middle");
    label.setAttribute("class", "tornado-axis-label");
    label.textContent = year === 0 ? "now" : `${year}y`;
    group.append(label);
  }

  const never = document.createElementNS(SVG_NS, "text");
  never.setAttribute("x", xScale(neverPos(horizonYears)).toFixed(2));
  never.setAttribute("y", String(y));
  never.setAttribute("text-anchor", "middle");
  never.setAttribute("class", "tornado-axis-label tornado-axis-never");
  never.textContent = "never";
  group.append(never);

  svg.append(group);
}

function drawBaselineMarker(
  svg: SVGSVGElement,
  baselineX: number,
  rowCount: number,
  baselineYear: number | null,
): void {
  const marker = document.createElementNS(SVG_NS, "line");
  marker.setAttribute("class", "tornado-baseline");
  marker.setAttribute("x1", baselineX.toFixed(2));
  marker.setAttribute("x2", baselineX.toFixed(2));
  marker.setAttribute("y1", String(LAYOUT.padTop));
  marker.setAttribute("y2", String(LAYOUT.padTop + rowCount * ROW_HEIGHT));
  svg.append(marker);

  const label = document.createElementNS(SVG_NS, "text");
  label.setAttribute("class", "tornado-baseline-label");
  // Anchor the baseline label so it doesn't spill off either edge of the
  // viewBox — right-align when the baseline is at "never" (right edge),
  // left-align when it's at year 0, centered elsewhere.
  const anchor =
    baselineX > LAYOUT.width - 40 ? "end" : baselineX < 40 ? "start" : "middle";
  const shift = anchor === "end" ? -2 : anchor === "start" ? 2 : 0;
  label.setAttribute("x", (baselineX + shift).toFixed(2));
  label.setAttribute("y", String(LAYOUT.padTop - 2));
  label.setAttribute("text-anchor", anchor);
  label.textContent = baselineYear === null ? "baseline: never" : `baseline: yr ${baselineYear}`;
  svg.append(label);
}

function drawRow(
  svg: SVGSVGElement,
  row: SensitivityRow,
  index: number,
  xScale: (year: number) => number,
  horizonYears: number,
): void {
  const y = LAYOUT.padTop + index * ROW_HEIGHT;

  const label = document.createElementNS(SVG_NS, "text");
  label.setAttribute("class", "tornado-row-label");
  label.setAttribute("x", "8");
  label.setAttribute("y", String(y + LAYOUT.rowLabelHeight - 2));
  label.textContent = `${row.label} ${formatDelta(row.delta, row.unit)}`;
  svg.append(label);

  const barY = y + LAYOUT.rowLabelHeight;
  const lowYearPos = yearPos(row.low, horizonYears);
  const highYearPos = yearPos(row.high, horizonYears);
  const lowX = xScale(lowYearPos);
  const highX = xScale(highYearPos);
  const barStart = Math.min(lowX, highX);
  const barEnd = Math.max(lowX, highX);

  if (barEnd - barStart < 2) {
    // Point: both perturbations produced the same break-even year (a
    // common outcome for a rent-favored default scenario where both ends
    // are "never"). Render as a dot with a small text label so the row is
    // never silent.
    const dot = document.createElementNS(SVG_NS, "circle");
    dot.setAttribute("class", "tornado-bar-point");
    dot.setAttribute("cx", barStart.toFixed(2));
    dot.setAttribute("cy", (barY + LAYOUT.rowBarHeight / 2).toFixed(2));
    dot.setAttribute("r", "3");
    svg.append(dot);

    const yearText = document.createElementNS(SVG_NS, "text");
    yearText.setAttribute("class", "tornado-bar-end");
    // Anchor to the opposite side of the point from the viewBox edge.
    const anchor = barStart > LAYOUT.width - 60 ? "end" : "start";
    const shift = anchor === "end" ? -8 : 8;
    yearText.setAttribute("x", (barStart + shift).toFixed(2));
    yearText.setAttribute("y", (barY + LAYOUT.rowBarHeight / 2 + 4).toFixed(2));
    yearText.setAttribute("text-anchor", anchor);
    yearText.textContent = `both ends: ${formatYear(row.low, horizonYears)}`;
    svg.append(yearText);
    return;
  }

  const bar = document.createElementNS(SVG_NS, "rect");
  bar.setAttribute("class", "tornado-bar");
  bar.setAttribute("x", barStart.toFixed(2));
  bar.setAttribute("y", barY.toFixed(2));
  bar.setAttribute("width", (barEnd - barStart).toFixed(2));
  bar.setAttribute("height", String(LAYOUT.rowBarHeight));
  bar.setAttribute("rx", "3");
  svg.append(bar);

  // `low` = value at (lever − delta); `high` = value at (lever + delta).
  // The two ends can land in either horizontal order depending on which
  // way each lever perturbs break-even; the annotation reflects that.
  drawEndAnnotation(svg, {
    x: lowX,
    y: barY + LAYOUT.rowBarHeight / 2 + 4,
    text: `${signedLabel(-row.delta, row.unit)}: ${formatYear(row.low, horizonYears)}`,
    onLeftEnd: lowX <= highX,
  });
  drawEndAnnotation(svg, {
    x: highX,
    y: barY + LAYOUT.rowBarHeight / 2 + 4,
    text: `${signedLabel(+row.delta, row.unit)}: ${formatYear(row.high, horizonYears)}`,
    onLeftEnd: highX < lowX,
  });
}

/**
 * Position a bar-end annotation just outside the bar's endpoint. `onLeftEnd`
 * means "this text sits at the LEFT end of the bar," so it right-aligns
 * (extends leftward off the bar). The opposite end left-aligns.
 */
function drawEndAnnotation(
  svg: SVGSVGElement,
  spec: { x: number; y: number; text: string; onLeftEnd: boolean },
): void {
  const label = document.createElementNS(SVG_NS, "text");
  label.setAttribute("class", "tornado-bar-end");
  const shift = spec.onLeftEnd ? -6 : 6;
  label.setAttribute("x", (spec.x + shift).toFixed(2));
  label.setAttribute("y", spec.y.toFixed(2));
  label.setAttribute("text-anchor", spec.onLeftEnd ? "end" : "start");
  label.textContent = spec.text;
  svg.append(label);
}

function formatDelta(delta: number, unit: string): string {
  return `±${trimNumber(delta)}${unit}`;
}

function signedLabel(delta: number, unit: string): string {
  const sign = delta >= 0 ? "+" : "−";
  return `${sign}${trimNumber(Math.abs(delta))}${unit}`;
}

function trimNumber(value: number): string {
  const s = value.toFixed(1);
  return s.endsWith(".0") ? s.slice(0, -2) : s;
}

function formatYear(year: number | null, horizonYears: number): string {
  if (year === null) return "never";
  if (year > horizonYears) return "never";
  return `yr ${year}`;
}

function describeChart(spec: TornadoSpec, sorted: readonly SensitivityRow[]): string {
  const baseline = sorted[0]?.baseline;
  const baselineText = baseline === null ? "never within horizon" : `year ${baseline}`;
  const rowDescriptions = sorted.map((row) => {
    const lowText = formatYear(row.low, spec.horizonYears);
    const highText = formatYear(row.high, spec.horizonYears);
    return `${row.label} ${signedLabel(-row.delta, row.unit)} ${lowText}, ${signedLabel(row.delta, row.unit)} ${highText}`;
  });
  return `Sensitivity of break-even year (${baselineText}): ${rowDescriptions.join("; ")}.`;
}

export const __tornadoInternals = {
  LAYOUT,
  ROW_HEIGHT,
  makeXScale,
  yearPos,
  sortBySensitivity,
  allDegenerate,
};
